import GenericForm from "../GenericForm";
import { PriceApi, PriceDataType } from "@/api/PricesApi";
import { PriceFormData } from "@/formTypes/PriceDataType";
import PriceFormConfig from "./form";
import { Button, Card, CardText, Col, Container, Input, InputGroup, InputGroupText, Label, Row } from "reactstrap";
import { DynamicFormRef } from "@mavichpe/dynamic-forms";
import { useRef, useState } from "react";
import styles from "./style.module.scss";
import cx from "classnames";
import { calculateGain } from "@utils/EDocument";

type PriceFormProps = {
  id?: string;
  formId: string,
  saveRedirectUrl: string
}

type CostCalculatorProps = {
  price: string;
  taxes: string,
  netPrice: string
}

const PriceForm = (props: PriceFormProps) => {
  const formRef = useRef<DynamicFormRef>(null);
  const [gain, setGain] = useState<String>("1");
  const [cost, setCost] = useState<String>("1000");
  const [example, setExample] = useState<CostCalculatorProps>({
    price: "0",
    taxes: "0",
    netPrice: "0",
  });

  const updateFormula = async (text: string) => {
    let value = text;
    if (text == "%") {
      const gainValue = Number(gain);
      if (!isNaN(gainValue)) {
        value = `${1 + (gainValue / 100)}`
      }
    }
    const data = await formRef.current?.getFormData(false);
    const { formula = '' } = data;
    formRef.current?.setFormValue("formula", `${formula}${value}`, "String");
  }

  const calculateExample = async () => {
    const data = await formRef.current?.getFormData(false);
    let formulaExpresion = data.formula;
    if (formulaExpresion) {
      let exampleCost = Number(cost);
      if (isNaN(exampleCost)) {
        exampleCost = Number(0);
        setCost("0")
      }
      let subtotal = calculateGain(formulaExpresion, exampleCost);
      subtotal = Number(subtotal);
      let impuestos = Number(subtotal * 0.13);
      let total = subtotal + impuestos;
      setExample({
        price: subtotal.toFixed(2),
        taxes: impuestos.toFixed(2),
        netPrice: total.toFixed(2),
      });
    } else {
      setExample({
        price: "0",
        taxes: "0",
        netPrice: "0",
      });
    }
  }

  return (
    <GenericForm<PriceFormData, PriceDataType>
      id={props.id}
      ref={formRef}
      formId={props.formId}
      saveRedirectUrl={props.saveRedirectUrl}
      formConfig={PriceFormConfig}
      getById={(...arg) => PriceApi.get(...arg)}
      createFn={(...arg) => PriceApi.create(...arg)}
      updateFn={(...arg) => PriceApi.update(...arg)}
      successMessage="El precio ha sido almacenada correctamente"
      errorMessage="No fue posible almacenar la información del precio"
    >
      <Container>
        <Col
          lg={{ offset: "2", size: "10" }}
          md={{ offset: "3", size: "9" }}
          sm={{ offset: "3" }}
        >
          <Col lg="4" md="5" sm="7" xs="11">
            <InputGroup className="mb-2">
              <InputGroupText>
                Porcentaje Ganancia
              </InputGroupText>
              <Input onChange={(e) => setGain(e.target.value)} value={`${gain}`} type="number" min={1} />
              <InputGroupText>
                %
              </InputGroupText>
            </InputGroup>
          </Col>
          <Row className="row-cols-lg-auto g-1">
            <Col>
              <Button type="button" onClick={() => updateFormula(" ( ")} >(</Button>
            </Col>
            <Col>
              <Button type="button" onClick={() => updateFormula(" ) ")} >)</Button>
            </Col>
            <Col>
              <Button type="button" onClick={() => updateFormula(" ) ")} >)</Button>
            </Col>
            <Col>
              <Button type="button" onClick={() => updateFormula(" costo ")} >Costo</Button>
            </Col>
            <Col>
              <Button type="button" onClick={() => updateFormula("%")} >Ganancia</Button>
            </Col>
            <Col>
              <Button type="button" onClick={() => updateFormula(" X ")} >X</Button>
            </Col>
            <Col>
              <Button type="button" onClick={() => updateFormula(" + ")} >+</Button>
            </Col>
            <Col>
              <Button type="button" onClick={() => updateFormula(" / ")} >/</Button>
            </Col>
            <Col>
              <Button type="button" onClick={() => updateFormula(" - ")} >-</Button>
            </Col>
          </Row>
          <Row className="g-1">
            <Col md="9">
              <Card
                className="mt-3"
                body
                color="light"
              >
                <CardText>
                  <h4>Ejemplo de Calculo de formula</h4>
                  <div className={cx([styles.calculatorRow, 'mb-1'])}>
                    <Label md="4" className="text-end">
                      Costo de producto
                    </Label>
                    <Col md="6">
                      <InputGroup className="mb-2">
                        <InputGroupText>
                          ₡
                        </InputGroupText>
                        <Input className={styles.input} onChange={(e) => setCost(e.target.value)} value={`${cost}`} type="number" min={1} />
                        <InputGroupText className="p-0">
                          <Button type="button" color="primary" onClick={() => calculateExample()} >Calcular</Button>
                        </InputGroupText>
                      </InputGroup>
                    </Col>
                  </div>
                  <div className={cx([styles.calculatorRow, 'mb-1'])}>
                    <Label md="4" className="text-end">
                      Costo de producto
                    </Label>
                    <Col md="6">
                      <InputGroup className="mb-2">
                        <InputGroupText>
                          ₡
                        </InputGroupText>
                        <Input className={styles.input} disabled value={`${example.price}`} type="number" min={1} />
                      </InputGroup>
                    </Col>
                  </div>
                  <div className={cx([styles.calculatorRow, 'mb-1'])}>
                    <Label md="4" className="text-end">
                      Impuestos
                    </Label>
                    <Col md="6">
                      <InputGroup className="mb-2">
                        <InputGroupText>
                          ₡
                        </InputGroupText>
                        <Input className={styles.input} disabled value={`${example.taxes}`} type="number" min={1} />
                      </InputGroup>
                    </Col>
                  </div>
                  <div className={cx([styles.calculatorRow, 'mb-1'])}>
                    <Label md="4" className="text-end">
                      Precio Neto
                    </Label>
                    <Col md="6">
                      <InputGroup className="mb-2">
                        <InputGroupText>
                          ₡
                        </InputGroupText>
                        <Input className={styles.input} disabled value={`${example.netPrice}`} type="number" min={1} />
                      </InputGroup>
                    </Col>
                  </div>
                </CardText>
              </Card>
            </Col>
          </Row>
        </Col>
      </Container>
    </GenericForm>
  )
}

export default PriceForm;